import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Image,
  Input,
  Link,
  Portal,
  Text,
} from "@chakra-ui/react";
import { Sparkles, Building2 } from "lucide-react";
import {
  EyebrowBadge,
  SectionCard,
  SelectableCard,
  FormField,
  OnboardingSelect,
  OnboardingComboBox,
  ToggleChoice,
} from "../components";
import {
  tipoCuentaCollection,
  tipoDocumentoTitularCollection,
  ONBOARDING_LINKS,
} from "../constants";
import type { CatalogItem, FormData } from "../types";

export function Step3Collection({
  data,
  banks,
  updateData,
  fieldErrors,
}: {
  data: FormData;
  banks: CatalogItem[];
  updateData: (field: string, value: string | boolean) => void;
  fieldErrors: Record<string, string>;
}) {
  return (
    <Box spaceY={8}>
      <Box spaceY={3}>
        <EyebrowBadge stepId={3} />
        <Box spaceY={2}>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color="gray.900"
            letterSpacing="tight"
          >
            Modelo de recaudo
          </Text>
          <Text fontSize="lg" color="gray.500" maxW="2xl">
            Elige cómo quieres operar el recaudo con Palomma y a qué cuenta
            debemos dispersar el dinero.
          </Text>
        </Box>
      </Box>

      <SectionCard
        title="Elige tu modelo"
        description="Puedes empezar más rápido con agregador o conectar tu propio convenio."
      >
        <Box spaceY={5}>
          <Box
            display="grid"
            gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={4}
          >
            <SelectableCard
              selected={data.modeloRecaudo}
              onSelect={(val) => updateData("modeloRecaudo", val)}
              id="agregador"
              title="Agregador"
              description="Usa nuestros convenios. Sin trámites bancarios y con activación más rápida."
              icon={Sparkles}
            />
            <SelectableCard
              selected={data.modeloRecaudo}
              onSelect={(val) => updateData("modeloRecaudo", val)}
              id="gateway"
              title="Gateway (PSE propio)"
              description="Conecta tu propio convenio PSE. Requiere configuración bancaria y código de servicio."
              icon={Building2}
            />
          </Box>

          {data.modeloRecaudo === "gateway" && (
            <SectionCard
              title="Código de servicio PSE"
              description="Si ya tienes el código, ingrésalo aquí. Si no sabes cómo crearlo o dónde encontrarlo, revisa la guía paso a paso."
            >
              <Box spaceY={4}>
                <Flex
                  flexDirection={{ base: "column", lg: "row" }}
                  gap={4}
                  alignItems={{ lg: "flex-end" }}
                  justifyContent={{ lg: "space-between" }}
                >
                  <Box w="full" maxW="xl">
                    <FormField
                      label="Código de servicio"
                      error={fieldErrors.codigoPse}
                    >
                      <Input
                        value={data.codigoPse}
                        onChange={(e) =>
                          updateData("codigoPse", e.target.value)
                        }
                        placeholder="1002"
                      />
                    </FormField>
                  </Box>
                  <Dialog.Root>
                    <Dialog.Trigger asChild>
                      <Button variant="outline" size="lg">
                        Ver guía paso a paso
                      </Button>
                    </Dialog.Trigger>
                    <Portal>
                      <Dialog.Backdrop />
                      <Dialog.Positioner>
                        <Dialog.Content maxW="3xl" maxH="85vh" overflowY="auto">
                          <Dialog.Header
                            borderBottom="1px solid"
                            borderColor="gray.100"
                            p={6}
                            flexDir="column"
                            alignItems="flex-start"
                          >
                            <Dialog.Title fontSize="lg" fontWeight="bold">
                              ¿Cómo crear tu código de servicio en PSE?
                            </Dialog.Title>
                          </Dialog.Header>
                          <Dialog.CloseTrigger asChild>
                            <CloseButton
                              position="absolute"
                              top={3}
                              right={3}
                            />
                          </Dialog.CloseTrigger>
                          <Dialog.Body py={6} spaceY={5}>
                            <Flex
                              flexDirection={{ base: "column", sm: "row" }}
                              flexWrap="wrap"
                              gap={2}
                              alignItems={{ sm: "center" }}
                            >
                              <Button asChild size="sm" colorPalette="palomma">
                                <a
                                  href={ONBOARDING_LINKS.gatewayVideo}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Ver tutorial
                                </a>
                              </Button>
                              <Button asChild variant="outline" size="sm">
                                <a
                                  href={ONBOARDING_LINKS.psePortal}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Abrir portal PSE
                                </a>
                              </Button>
                            </Flex>
                            <Box
                              as="ol"
                              ml={5}
                              spaceY={3}
                              fontSize="sm"
                              color="gray.600"
                              lineHeight="tall"
                              listStyleType="decimal"
                            >
                              <Text as="li">
                                Ingresa al portal PSE con un usuario
                                administrador.
                              </Text>
                              <Text as="li">
                                El número de código de servicio es el que te
                                vamos a pedir. Puedes configurarlo como 1002 o
                                similar.
                              </Text>
                              <Text as="li">
                                El nombre del código es opcional. Te
                                recomendamos dejarlo como{" "}
                                <strong>PALOMMA</strong>.
                              </Text>
                              <Text as="li">
                                Durante el proceso, selecciona que el recaudo es
                                a través de una pasarela.
                              </Text>
                              <Text as="li">
                                En la lista de pasarelas, elige{" "}
                                <strong>Palomma S.A.S</strong>.
                              </Text>
                              <Text as="li">
                                El tipo de recaudo es <strong>Normal</strong>.
                              </Text>
                            </Box>
                            <Box
                              spaceY={3}
                              borderRadius="lg"
                              borderWidth="1px"
                              borderColor="gray.200"
                              bg="gray.50"
                              p={4}
                            >
                              <Flex
                                flexDirection={{ base: "column", sm: "row" }}
                                gap={3}
                                alignItems={{ sm: "center" }}
                                justifyContent={{ sm: "space-between" }}
                              >
                                <Box>
                                  <Text
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.500"
                                    textTransform="uppercase"
                                    letterSpacing="wider"
                                  >
                                    Referencia visual
                                  </Text>
                                  <Text fontSize="sm" color="gray.600" mt={1}>
                                    Este es el valor que necesitamos: el número
                                    del código de servicio. Puedes usar 1002 o
                                    un valor similar.
                                  </Text>
                                </Box>
                                <Button
                                  asChild
                                  variant="outline"
                                  size="sm"
                                  flexShrink={0}
                                >
                                  <a
                                    href="/pse-service-code-guide.png"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Abrir imagen completa
                                  </a>
                                </Button>
                              </Flex>
                              <Link
                                href="/pse-service-code-guide.png"
                                target="_blank"
                                rel="noreferrer"
                                display="block"
                                overflow="hidden"
                                borderRadius="lg"
                                borderWidth="1px"
                                borderColor="gray.200"
                                bg="white"
                              >
                                <Image
                                  src="/pse-service-code-guide.png"
                                  alt="Referencia visual para crear el código de servicio en PSE"
                                  maxH="26rem"
                                  w="full"
                                  objectFit="contain"
                                />
                              </Link>
                            </Box>
                            <Text fontSize="sm" color="gray.700">
                              Listo. Ese código es el que usaremos para conectar
                              Palomma con tu convenio de PSE.
                            </Text>
                          </Dialog.Body>
                        </Dialog.Content>
                      </Dialog.Positioner>
                    </Portal>
                  </Dialog.Root>
                </Flex>
                <Text maxW="xl" fontSize="sm" color="gray.500">
                  Por ejemplo: 1002. Puedes continuar sin este código y
                  compartirlo con nuestro equipo más adelante.
                </Text>
              </Box>
            </SectionCard>
          )}
        </Box>
      </SectionCard>

      <SectionCard
        title="Cuenta de dispersión"
        description="A esta cuenta bancaria enviaremos el dinero recaudado."
      >
        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "1.1fr 0.75fr 1.15fr" }}
          gap={6}
        >
          <OnboardingComboBox
            label="Banco"
            items={banks}
            value={data.banco}
            onValueChange={(value) => updateData("banco", value)}
            error={fieldErrors.banco}
            placeholder="Buscar banco..."
          />
          <OnboardingSelect
            label="Tipo de cuenta"
            collection={tipoCuentaCollection}
            value={data.tipoCuenta}
            onValueChange={({ value }) => updateData("tipoCuenta", value[0])}
            error={fieldErrors.tipoCuenta}
          />
          <FormField label="Número de cuenta" error={fieldErrors.numeroCuenta}>
            <Input
              value={data.numeroCuenta}
              onChange={(e) => updateData("numeroCuenta", e.target.value)}
              placeholder="000-000000-00"
            />
          </FormField>
        </Box>
        <Box mt={6} spaceY={4}>
          <ToggleChoice
            value={data.titularCuentaEsComercio ? "si" : "no"}
            onChange={(value) =>
              updateData("titularCuentaEsComercio", value === "si")
            }
            title="¿La cuenta pertenece al comercio?"
          />
          {!data.titularCuentaEsComercio && (
            <Box
              display="grid"
              gridTemplateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }}
              gap={6}
            >
              <FormField
                label="Nombre del titular"
                error={fieldErrors.titularCuentaNombre}
              >
                <Input
                  value={data.titularCuentaNombre}
                  onChange={(e) =>
                    updateData("titularCuentaNombre", e.target.value)
                  }
                  placeholder="Nombre completo o razón social"
                />
              </FormField>
              <OnboardingSelect
                label="Tipo de documento"
                collection={tipoDocumentoTitularCollection}
                value={data.titularCuentaTipoDocumento}
                onValueChange={({ value }) =>
                  updateData("titularCuentaTipoDocumento", value[0])
                }
                error={fieldErrors.titularCuentaTipoDocumento}
              />
              <FormField
                label="Número de documento"
                error={fieldErrors.titularCuentaNumeroDocumento}
              >
                <Input
                  value={data.titularCuentaNumeroDocumento}
                  onChange={(e) =>
                    updateData("titularCuentaNumeroDocumento", e.target.value)
                  }
                  placeholder="Número de identificación"
                />
              </FormField>
            </Box>
          )}
        </Box>
      </SectionCard>
    </Box>
  );
}
